// Magazzino centrale con overview stock, alert sotto-scorta e accesso ai sotto-moduli
import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Package, Truck, ArrowLeftRight, BarChart2, AlertTriangle, Plus, Pencil, Check, X } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { Table } from '../../components/ui/Table'
import { Alert } from '../../components/ui/Alert'
import { ContextualHelp } from '../../components/shared/ContextualHelp'
import { formatDate } from '../../lib/utils'
import api from '../../lib/api'

interface WarehouseOverview {
  totalItems: number
  lowStockCount: number
  totalValue: number
  lastMovement: string
}

interface StockItem {
  id: string
  sku?: string
  name: string
  category?: string
  quantity?: number
  minQuantity?: number
  location?: string
  unit?: string
}

const QUICK_ACCESS = [
  { label: 'Materiali', icon: <Package className="h-5 w-5" />, href: '/warehouse/materials', color: 'text-[var(--fc-primary)]', bg: 'bg-[color-mix(in_srgb,var(--fc-primary)_10%,transparent)]' },
  { label: 'Movimenti', icon: <ArrowLeftRight className="h-5 w-5" />, href: '/warehouse/movements', color: 'text-[var(--fc-success)]', bg: 'bg-[color-mix(in_srgb,var(--fc-success)_10%,transparent)]' },
  { label: 'Stock furgoni', icon: <Truck className="h-5 w-5" />, href: '/warehouse/van-stocks', color: 'text-[var(--fc-warning)]', bg: 'bg-[color-mix(in_srgb,var(--fc-warning)_10%,transparent)]' },
  { label: 'Inventario', icon: <BarChart2 className="h-5 w-5" />, href: '/warehouse/inventory', color: 'text-[var(--fc-info)]', bg: 'bg-[color-mix(in_srgb,var(--fc-info)_10%,transparent)]' },
]

// Cella giacenza con editing inline
function QuantityCell({ item, warehouseId, onSaved }: { item: StockItem; warehouseId: string; onSaved: () => void }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(String(item.quantity ?? 0))
  const inputRef = useRef<HTMLInputElement>(null)

  const mutation = useMutation({
    mutationFn: (qty: number) =>
      api.patch(`/warehouse/locations/${warehouseId}/stock/${item.id}`, { quantity: qty }),
    onSuccess: () => { setEditing(false); onSaved() },
  })

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5 group">
        <span className={`font-semibold ${(item.quantity ?? 0) <= (item.minQuantity ?? 0) ? 'text-[var(--fc-danger)]' : 'text-[var(--fc-foreground)]'}`}>
          {item.quantity ?? 0} {item.unit ?? ''}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); setEditing(true); setTimeout(() => inputRef.current?.select(), 30) }}
          className="opacity-0 group-hover:opacity-100 rounded p-0.5 text-[var(--fc-foreground-muted)] hover:text-[var(--fc-primary)] transition-opacity"
          title="Modifica giacenza"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef}
        type="number"
        min={0}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') mutation.mutate(parseFloat(value) || 0)
          if (e.key === 'Escape') setEditing(false)
        }}
        className="w-20 rounded border border-[var(--fc-primary)] bg-[var(--fc-surface)] px-2 py-0.5 text-sm font-semibold text-[var(--fc-foreground)] focus:outline-none"
        autoFocus
      />
      <span className="text-xs text-[var(--fc-foreground-muted)]">{item.unit ?? ''}</span>
      <button
        onClick={() => mutation.mutate(parseFloat(value) || 0)}
        disabled={mutation.isPending}
        className="rounded p-0.5 text-[var(--fc-success)] hover:bg-[color-mix(in_srgb,var(--fc-success)_10%,transparent)]"
        title="Salva"
      >
        <Check className="h-4 w-4" />
      </button>
      <button
        onClick={() => setEditing(false)}
        className="rounded p-0.5 text-[var(--fc-foreground-muted)] hover:bg-[var(--fc-surface-overlay)]"
        title="Annulla"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

export function WarehousePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>('00000000-0000-0000-0000-000000000001')

  const { data: warehouses = [] } = useQuery<any[]>({
    queryKey: ['warehouses'],
    queryFn: () => api.get('/warehouse/locations').then(r => r.data),
  })

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['warehouse'] })
  }

  const { data: overview } = useQuery({
    queryKey: ['warehouse', 'overview'],
    queryFn: async () => {
      try {
        const res = await api.get<WarehouseOverview>('/warehouse/overview')
        return res.data
      } catch {
        // endpoint non disponibile — costruisce overview dai materiali
        return null
      }
    },
  })

  const { data: lowStock = [], isLoading: isLoadingLow } = useQuery({
    queryKey: ['warehouse', 'low-stock'],
    queryFn: async () => {
      const res = await api.get<StockItem[] | { items?: StockItem[] }>('/warehouse/materials', { params: { lowStock: true, limit: 10 } })
      const payload = res.data
      const items = Array.isArray(payload) ? payload : (Array.isArray((payload as { items?: StockItem[] })?.items) ? (payload as { items: StockItem[] }).items : [])
      // Filtra lato client per sotto-scorta se il backend non supporta il filtro
      return items.filter((i) => (i.quantity ?? 0) <= (i.minQuantity ?? 0))
    },
  })

  const { data: recent = [], isLoading: isLoadingRecent } = useQuery({
    queryKey: ['warehouse-materials', selectedWarehouseId],
    queryFn: () => api.get(`/warehouse/materials?warehouseId=${selectedWarehouseId}`).then(r => r.data),
  })

  const columns = [
    {
      key: 'sku',
      header: 'SKU',
      cell: (item: StockItem) => <span className="font-mono text-xs">{item.sku ?? '—'}</span>,
      className: 'w-28',
    },
    {
      key: 'name',
      header: 'Nome materiale',
      cell: (item: StockItem) => <span className="font-medium text-[var(--fc-foreground)]">{item.name}</span>,
    },
    {
      key: 'category',
      header: 'Categoria',
      cell: (item: StockItem) => <Badge variant="default">{item.category ?? '—'}</Badge>,
      className: 'w-32',
    },
    {
      key: 'quantity',
      header: 'Giacenza',
      cell: (item: StockItem) => <QuantityCell item={item} warehouseId={selectedWarehouseId} onSaved={invalidate} />,
      className: 'w-36',
    },
    {
      key: 'minQuantity',
      header: 'Scorta min.',
      cell: (item: StockItem) => <span className="text-[var(--fc-foreground-muted)]">{item.minQuantity ?? 0} {item.unit ?? ''}</span>,
      className: 'w-24',
    },
    {
      key: 'location',
      header: 'Ubicazione',
      cell: (item: StockItem) => <span className="text-sm text-[var(--fc-foreground-muted)]">{item.location ?? '—'}</span>,
      className: 'w-28',
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Magazzino"
        subtitle="Gestione materiali, stock furgoni e movimenti"
        actions={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => navigate('/warehouse/materials')}>
            Nuovo materiale
          </Button>
        }
      />

      {/* Selettore magazzino */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <span className="text-sm font-medium text-[var(--fc-foreground-muted)]">Magazzino:</span>
        <div className="flex gap-2 flex-wrap">
          {warehouses.map((wh: any) => (
            <button
              key={wh.id}
              onClick={() => setSelectedWarehouseId(wh.id)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                selectedWarehouseId === wh.id
                  ? 'bg-[var(--fc-primary)] text-white'
                  : 'bg-[color-mix(in_srgb,var(--fc-foreground)_8%,transparent)] text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)]'
              }`}
            >
              {wh.type === 'central' ? '🏭 ' : '📦 '}{wh.name}
            </button>
          ))}
        </div>
      </div>

      {/* KPI overview */}
      {overview && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[
            { label: 'Articoli totali', value: overview.totalItems },
            { label: 'Sotto scorta', value: overview.lowStockCount, danger: true },
            { label: 'Valore totale', value: `€${overview.totalValue?.toLocaleString('it-IT') ?? 0}` },
            { label: 'Ultimo movimento', value: overview.lastMovement ? formatDate(overview.lastMovement, 'dd/MM/yyyy') : 'N/D' },
          ].map(({ label, value, danger }) => (
            <Card key={label}>
              <p className="text-xs text-[var(--fc-foreground-muted)]">{label}</p>
              <p className={`mt-1 text-2xl font-bold ${danger && Number(value) > 0 ? 'text-[var(--fc-danger)]' : 'text-[var(--fc-foreground)]'}`}>
                {value}
              </p>
            </Card>
          ))}
        </div>
      )}

      {/* Accesso rapido */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {QUICK_ACCESS.map((item) => (
          <button
            key={item.label}
            onClick={() => navigate(item.href)}
            className="flex flex-col items-center gap-2 rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] p-4 hover:border-[var(--fc-primary)] hover:shadow-[var(--fc-shadow)] transition-all"
            aria-label={item.label}
          >
            <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${item.bg}`}>
              <span className={item.color}>{item.icon}</span>
            </div>
            <span className="text-sm font-medium text-[var(--fc-foreground)]">{item.label}</span>
          </button>
        ))}
      </div>

      {/* Alert sotto-scorta */}
      {!isLoadingLow && lowStock.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-[var(--fc-warning)]" aria-hidden="true" />
            <h2 className="text-base font-semibold text-[var(--fc-foreground)]">
              Materiali sotto scorta ({lowStock.length})
            </h2>
          </div>
          <Alert variant="warning" title="Attenzione">
            {lowStock.length} materiali hanno raggiunto o superato la soglia minima di scorta.
          </Alert>
          <Table
            data={lowStock}
            columns={columns}
            keyExtractor={(item) => item.id}
            onRowClick={(item) => navigate(`/warehouse/materials/${item.id}`)}
          />
        </div>
      )}

      {/* Tutti i materiali */}
      <div>
        <h2 className="mb-3 text-base font-semibold text-[var(--fc-foreground)]">Materiali recenti</h2>
        <Table
          data={recent}
          columns={columns}
          keyExtractor={(item) => item.id}
          isLoading={isLoadingRecent}
          onRowClick={(item) => navigate(`/warehouse/materials/${item.id}`)}
          emptyMessage="Nessun materiale in magazzino"
        />
      </div>

      <ContextualHelp />
    </div>
  )
}
